/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  SxProps,
  Typography,
} from '@mui/material';
import { KeyboardArrowLeft } from '@mui/icons-material';

import { DateRangeInput } from 'components/custom-date-picker/date-range-input/date-range-input';
import { Colors } from 'constants/colors';
import { fontSecondary } from 'constants/font-family';
import { FontSizes } from 'constants/font-sizes';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import React, { useEffect } from 'react';
import { StyledButton } from 'components/button/button';
import dayjs, { ManipulateType } from 'dayjs';
import moment from 'moment';
import {
  DATE_PICKER_BUTTON,
  DATE_PICKER_CUSTOM_PERIOD,
  DATE_PICKER_MENU_ITEM,
  DATE_PICKER_SELECT,
} from 'constants/test-ids';
import { useDispatch } from 'react-redux';
import { useDatePickerSelector } from 'store/modules/date-picker/date-picker.selector';
import {
  setActivityEndDate,
  setActivityPeriod,
  setActivityStartDate,
  setCalendarTab,
} from 'store/modules/date-picker/date-picker.reducer';

export enum Periods {
  'weeks',
  'months',
  'years',
  'custom',
}

interface Props {
  id: string;
  tabsList: string[];
  minDate?: Date | null;
  alternateLabels?: string[];
  disableCustom?: boolean;
  iconOnly?: boolean;
  dataTestId?: string;
  style?: SxProps;
}

export const DatePicker = ({
  id,
  tabsList,
  minDate,
  alternateLabels,
  disableCustom,
  iconOnly = false,
  dataTestId = DATE_PICKER_SELECT,
  style = {},
}: Props) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const openMenu = Boolean(anchorEl);

  const { activityStartDate, activityEndDate, activityPeriod, calendarTab } =
    useDatePickerSelector(id);

  const tabValue = tabsList[calendarTab];
  const setTabValue = (tabValue: string): void => {
    const index = tabsList.indexOf(tabValue);
    dispatch(
      setCalendarTab({
        id,
        calendarTab: index,
      }),
    );
  };

  useEffect(() => {
    if (tabValue) onSelectPeriod(tabValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectPeriod = (period: string) => {
    setTabValue(period);

    if (period === 'custom') {
      dispatch(
        setActivityPeriod({
          id,
          activityPeriod: period,
        }),
      );
      return;
    }

    const duration = parseInt(period.split(' ')[1]);
    const units = period.split(' ')[2];
    const newStartDate = dayjs(new Date())
      .subtract(duration, units as ManipulateType)
      .format('YYYY-MM-DDTHH:mm:ss');
    if (!dayjs(newStartDate).isSame(activityStartDate, 'day'))
      dispatch(
        setActivityStartDate({
          id,
          activityStartDate: newStartDate,
        }),
      );
    const newEndDate = dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss');
    if (!dayjs(newEndDate).isSame(activityEndDate, 'day'))
      dispatch(
        setActivityEndDate({
          id,
          activityEndDate: newEndDate,
        }),
      );
    dispatch(
      setActivityPeriod({
        id,
        activityPeriod: Periods[1],
      }),
    );
  };

  const onSubmitHandler = () => {
    dispatch(
      setActivityStartDate({
        id,
        activityStartDate: startDate
          ? dayjs(startDate).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
      }),
    );
    dispatch(
      setActivityEndDate({
        id,
        activityEndDate: endDate
          ? dayjs(endDate).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
      }),
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsToggle(false);
    if (tabValue === 'custom' && !startDate) onSelectPeriod(tabsList[0]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [isToggle, setIsToggle] = React.useState(false);

  return (
    <Box data-testid={dataTestId} sx={style}>
      <IconButton
        id="period-button"
        aria-controls={openMenu ? 'period-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
        onClick={handleClick}
        sx={{
          minWidth: iconOnly ? '0px' : '120px',
          height: '36px',
          borderRadius: '5px',
          border: `1px solid ${Colors.blackPearl}`,
          padding: '5px 10px',
          gap: '5px',
        }}
        data-testid={DATE_PICKER_BUTTON}
      >
        <Typography
          sx={{
            color: Colors.blackPearl,
            fontFamily: fontSecondary,
            fontSize: FontSizes.fontSize14,
            lineHeight: '22px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CalendarIcon style={{ marginRight: iconOnly ? 0 : '5px' }} />
          {!iconOnly
            ? tabValue
              ? alternateLabels?.length
                ? alternateLabels[tabsList.indexOf(tabValue)]
                : tabValue
              : startDate && endDate
              ? startDate.getFullYear() === endDate.getFullYear()
                ? startDate.getMonth() === endDate.getMonth()
                  ? `${moment(startDate).format('MMM DD')} - ${moment(
                      endDate,
                    ).format('DD, YYYY')}`
                  : `${moment(startDate).format('MMM DD')} - ${moment(
                      endDate,
                    ).format('MMM DD, YYYY')}`
                : `${moment(startDate).format('MMM DD, YYYY')} - ${moment(
                    endDate,
                  ).format('MMM DD, YYYY')}`
              : 'Custom'
            : null}
        </Typography>
      </IconButton>
      <Menu
        id="period-menu"
        MenuListProps={{
          'aria-labelledby': 'period-button',
        }}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        sx={{
          padding: '0px',
          top: '8px',
          left: '0px',
          '& .MuiList-padding': {
            padding: '0px',
            borderRadius: '10px',
          },
        }}
      >
        <MenuList
          sx={{
            backgroundColor: Colors.white,
            padding: '0px',
            gap: '10px',
            borderRadius: '10px',
            boxShadow: `0px 4px 4px 0px ${Colors.tooltipShadow}`,
            width: '119px',
          }}
        >
          {tabsList?.map((tab, index) => (
            <MenuItem onClick={handleClose} disableRipple key={tab + index}>
              <StyledButton
                onClick={() => onSelectPeriod(tab)}
                variant="text"
                color={'primary'}
                size="small"
                sx={{
                  padding: '0px',
                  color: `${
                    tabValue === tab
                      ? `${Colors.binge} !important`
                      : Colors.darkGray
                  }`,
                }}
                data-testid={DATE_PICKER_MENU_ITEM}
              >
                {alternateLabels?.length ? alternateLabels[index] : tab}
              </StyledButton>
            </MenuItem>
          ))}
          {!disableCustom ? (
            <MenuItem disableRipple>
              <StyledButton
                onClick={() => {
                  onSelectPeriod('custom');
                  setIsToggle(true);
                }}
                variant="text"
                color={'primary'}
                size="small"
                sx={{
                  color: `${
                    tabValue === 'custom'
                      ? `${Colors.binge} !important`
                      : Colors.darkGray
                  }`,
                }}
                startIcon={<KeyboardArrowLeft />}
                data-testid={DATE_PICKER_CUSTOM_PERIOD}
              >
                Custom
              </StyledButton>
            </MenuItem>
          ) : null}
        </MenuList>
      </Menu>
      {activityPeriod === 'custom' && isToggle && (
        <DateRangeInput
          startDate={startDate}
          endDate={endDate}
          minDate={minDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          onSubmitHandler={onSubmitHandler}
          isToggle={isToggle}
          setIsToggle={setIsToggle}
          resetDates={() => onSelectPeriod(tabsList[1])}
        />
      )}
    </Box>
  );
};
